/**
 * MicFieldButton — kleines Mikrofon-Icon, das neben einem Eingabefeld
 * platziert wird. Drückt der User darauf, startet eine Mediarecorder-
 * Aufnahme. Nach dem Stop wird via Whisper transkribiert und das
 * Ergebnis per ``onTranscript(text)`` an den Parent gereicht.
 *
 * Props:
 *  - onTranscript: (text: string) => void     // Pflicht
 *  - mode: "replace" | "append"               // default "append"
 *  - language: "de" | "en" | …                // default "de"
 *  - className?: string                       // wrapper-class override
 */
import React, { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function MicFieldButton({ onTranscript, language = "de", className = "", testid }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("language", language);
          const { data } = await api.post("/ai/transcribe", fd);
          const text = (data.text || "").trim();
          if (text) {
            onTranscript?.(text);
            toast.success("Sprache erkannt");
          } else {
            toast.info("Kein Text erkannt");
          }
        } catch (e) {
          toast.error(apiError(e, "Transkription fehlgeschlagen"));
        } finally {
          setProcessing(false);
        }
      };
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const handleClick = () => (recording ? stop() : start());

  const Icon = processing ? Loader2 : recording ? Square : Mic;

  return (
    <button
      type="button"
      aria-label={recording ? "Aufnahme stoppen" : "Diktat starten"}
      title={recording ? "Aufnahme stoppen" : "Diktat starten"}
      onClick={handleClick}
      disabled={processing}
      data-testid={testid || "mic-field-btn"}
      className={[
        "shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg transition-colors",
        recording
          ? "bg-rose-600 text-white hover:bg-rose-700 animate-pulse"
          : "bg-zinc-100 text-zinc-600 hover:bg-cyan-100 hover:text-cyan-700",
        processing ? "opacity-60 cursor-wait" : "",
        className,
      ].filter(Boolean).join(" ")}
    >
      <Icon className={`h-4 w-4 ${processing ? "animate-spin" : ""}`} strokeWidth={2} />
    </button>
  );
}
